import React from "react";
import { OverlayView } from "@react-google-maps/api";

/**
 * ClusterCountOverlay
 *
 * Renders a small badge above every active cluster showing the number of
 * markers it contains.  Drop it after your <MarkerClusterer> block and pass
 * the `clusters` array captured via `onClusteringEnd`.
 *
 * Usage:
 *   const [clusters, setClusters] = useState([]);
 *   ...
 *   <MarkerClusterer
 *     onClusteringEnd={(c) => setClusters(c.getClusters().filter(cl => Array.isArray(cl.markers)))}
 *     ...
 *   >
 *     ...
 *   </MarkerClusterer>
 *   <ClusterCountOverlay clusters={clusters} />
 *
 * @param {{ clusters: import('@react-google-maps/api').Cluster[] }} props
 */
export const ClusterCountOverlay = ({ clusters = [] }) =>
  clusters
    .filter((c) => Array.isArray(c.markers) && c.markers.length > 1)
    .map((cluster, i) => {
      const center = cluster.getCenter();
      if (!center) return null;
      return (
        <OverlayView
          key={i}
          position={center.toJSON()}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: "absolute",
              transform: "translate(-50%, -100%)",
              marginTop: "-40px",
              backgroundColor: "#072585ff",
              color: "#ffffff",
              borderRadius: "10px",
              padding: "2px 7px",
              fontSize: "11px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              pointerEvents: "none",
            }}
          >
            {cluster.markers.length}
          </div>
        </OverlayView>
      );
    });

/**
 * Utility function to fit Google Maps to show all stops and vehicles
 * @param {google.maps.Map} mapInstance - The Google Maps instance
 * @param {Array} stops - Array of stop objects with latitude/longitude
 * @param {Array} vehicleLocations - Array of vehicle location objects with latitude/longitude
 * @param {Object} options - Optional configuration
 * @param {number} options.maxZoom - Maximum zoom level (default: 19)
 * @param {Object} options.padding - Padding for fitBounds (default: 20px all sides)
 */
export const fitMapToStops = (mapInstance, stops = [], vehicleLocations = [], options = {}) => {
  if (!mapInstance || (!stops?.length && !vehicleLocations?.length)) {
    console.warn('fitMapToStops: Invalid mapInstance or no stops/vehicles provided');
    return;
  }

  const {
    maxZoom = 19,
    padding = { top: 20, right: 20, bottom: 20, left: 20 }
  } = options;

  const bounds = new window.google.maps.LatLngBounds();

  // Add all stops to bounds
  stops.forEach((stop) => {
    if (stop.latitude && stop.longitude) {
      const lat = parseFloat(stop.latitude);
      const lng = parseFloat(stop.longitude);
      
      // Validate coordinates
      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend({ lat, lng });
      }
    }
  });

  // Add vehicle locations to bounds if available
  vehicleLocations.forEach((vehicle) => {
    if (vehicle.latitude && vehicle.longitude) {
      const lat = parseFloat(vehicle.latitude);
      const lng = parseFloat(vehicle.longitude);
      
      // Validate coordinates
      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend({ lat, lng });
      }
    }
  });

  // Fit the map to the bounds with padding
  if (!bounds.isEmpty()) {
    mapInstance.fitBounds(bounds, padding);

    // Set a maximum zoom level to prevent over-zooming on single points
    const listener = window.google.maps.event.addListener(
      mapInstance,
      "bounds_changed",
      () => {
        if (mapInstance.getZoom() > maxZoom) {
          mapInstance.setZoom(maxZoom);
        }
        window.google.maps.event.removeListener(listener);
      }
    );
  } else {
    console.warn('fitMapToStops: No valid coordinates found to fit bounds');
  }
};


export const calculateDistance = (p1, p2) => {
  if (!p1 || !p2) return Infinity;
  const R = 6371e3;
  const φ1 = (p1.lat * Math.PI) / 180;
  const φ2 = (p2.lat * Math.PI) / 180;
  const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const findNearestPointOnRoute = (position, directions) => {
  if (!position || !directions?.routes?.[0]) return null;
  const route = directions.routes[0];
  let closestPoint = null;
  let minDistance = Infinity;
  let routeIndex = -1;
  let currentRouteIndex = 0;

  route.legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      const path = step.path;
      path.forEach((point, index) => {
        if (index === 0) return;
        const getLat = (p) => (typeof p.lat === "function" ? p.lat() : p.lat);
        const getLng = (p) => (typeof p.lng === "function" ? p.lng() : p.lng);

        const start = {
          lat: getLat(path[index - 1]),
          lng: getLng(path[index - 1]),
        };
        const end = {
          lat: getLat(point),
          lng: getLng(point),
        };

        const dx = end.lng - start.lng;
        const dy = end.lat - start.lat;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        if (segmentLength === 0) return;

        const t =
          ((position.lng - start.lng) * dx + (position.lat - start.lat) * dy) /
          (segmentLength * segmentLength);

        if (t < 0) {
          const distToStart = calculateDistance(position, start);
          if (distToStart < minDistance) {
            minDistance = distToStart;
            closestPoint = start;
            routeIndex = currentRouteIndex;
          }
          return;
        }

        if (t > 1) {
          const distToEnd = calculateDistance(position, end);
          if (distToEnd < minDistance) {
            minDistance = distToEnd;
            closestPoint = end;
            routeIndex = currentRouteIndex + 1;
          }
          return;
        }

        const projectedPoint = {
          lat: start.lat + t * dy,
          lng: start.lng + t * dx,
        };
        const distance = calculateDistance(position, projectedPoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = projectedPoint;
          routeIndex = currentRouteIndex + t;
        }

        currentRouteIndex++;
      });
    });
  });

  const MAX_SNAP_DISTANCE = 50;
  if (minDistance <= MAX_SNAP_DISTANCE) {
    return {
      ...closestPoint,
      routeIndex: routeIndex,
    };
  }
  return null;
};